/**
 * Analytics KPI Cards
 *
 * Displays 4 analytics-focused cards: YoY Reduction, Target Progress,
 * Carbon Intensity, and Top Hotspot.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MetricCard } from '@g17eco/ui-modern/components/ui/metric-card';
import { generateUrl } from '@routes/util';
import { emissionsTrackerRoutes } from '../routes';
import type { EmissionSummary, IntensityMetrics } from '../types/emissions';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';

interface AnalyticsKPICardsProps {
  initiativeId: string;
  summary?: EmissionSummary;
  previousSummary?: EmissionSummary;
  intensityMetrics?: IntensityMetrics;
  isLoading: boolean;
  previousLoading?: boolean;
  intensityLoading?: boolean;
}

export const AnalyticsKPICards = ({
  initiativeId,
  summary,
  previousSummary,
  intensityMetrics,
  isLoading,
  previousLoading,
  intensityLoading,
}: AnalyticsKPICardsProps) => {
  const { formatCompact } = useEmissionsFormatter();
  const total = summary?.totalCO2e || 0;
  const scope1 = summary?.byScope?.scope1 || 0;
  const scope2 = summary?.byScope?.scope2 || 0;
  const scope3 = summary?.byScope?.scope3 || 0;

  const yoyReduction = useMemo(() => {
    if (!summary || !previousSummary) return null;
    const prevTotal = previousSummary.totalCO2e || 0;
    if (prevTotal === 0 || previousSummary.activityCount === 0) return null;
    return ((total - prevTotal) / prevTotal) * 100;
  }, [total, previousSummary, summary]);

  const carbonIntensity = useMemo(() => {
    if (!intensityMetrics) return { value: null, unit: '', hasData: false };
    if (intensityMetrics.perEmployee !== null) {
      const v = intensityMetrics.perEmployee;
      return v >= 1000
        ? { value: (v / 1000).toFixed(2), unit: 'tCO₂e/employee', hasData: true }
        : { value: v.toFixed(1), unit: 'kgCO₂e/employee', hasData: true };
    }
    if (intensityMetrics.perRevenue !== null) {
      const v = intensityMetrics.perRevenue;
      return v >= 1000
        ? { value: (v / 1000).toFixed(2), unit: 'tCO₂e/M$', hasData: true }
        : { value: v.toFixed(1), unit: 'kgCO₂e/M$', hasData: true };
    }
    if (intensityMetrics.perFloorArea !== null) {
      const v = intensityMetrics.perFloorArea;
      return v >= 1000
        ? { value: (v / 1000).toFixed(2), unit: 'tCO₂e/m²', hasData: true }
        : { value: v.toFixed(1), unit: 'kgCO₂e/m²', hasData: true };
    }
    return { value: null, unit: '', hasData: false };
  }, [intensityMetrics]);

  const businessMetricsLink = generateUrl(emissionsTrackerRoutes.EMISSIONS_BUSINESS_METRICS, { initiativeId });

  const topHotspot = useMemo(() => {
    if (!summary || total === 0) return { label: 'N/A', percentage: 0 };
    const scopes = [
      { label: 'Scope 1', value: scope1 },
      { label: 'Scope 2', value: scope2 },
      { label: 'Scope 3', value: scope3 },
    ];
    const top = scopes.reduce((a, b) => (a.value > b.value ? a : b));
    return { label: top.label, percentage: Math.round((top.value / total) * 100) };
  }, [summary, total, scope1, scope2, scope3]);

  const showYoYSpinner = previousLoading && !previousSummary;

  const getYoYValue = (): string => {
    if (showYoYSpinner || isLoading) return '--';
    if (yoyReduction === null) return '--';
    return `${yoyReduction > 0 ? '+' : ''}${yoyReduction.toFixed(1)}%`;
  };

  const getYoYSubtext = (): string => {
    const prevTotal = previousSummary?.totalCO2e;
    if (prevTotal == null || !previousSummary || previousSummary.activityCount === 0) return 'No prior year data';
    return `Last year: ${formatCompact(prevTotal).display}`;
  };

  const isReduction = yoyReduction !== null && yoyReduction < 0;

  return (
    <div className='tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:lg:grid-cols-4 tw:gap-3'>
      <MetricCard
        label='YoY Change'
        value={getYoYValue()}
        subtext={getYoYSubtext()}
        icon={<i className={`fa ${isReduction ? 'fa-arrow-trend-down' : 'fa-arrow-trend-up'}`} style={{ color: isReduction ? '#28a745' : '#dc3545' }} />}
        iconBg={isReduction ? 'tw:bg-emerald-500/10' : 'tw:bg-red-500/10'}
        borderColor={isReduction ? 'emerald-500' : 'red-400'}
        isLoading={isLoading}
      />
      <MetricCard
        label='Target Progress'
        value='--'
        subtext='Set a target to track'
        icon={<i className='fa fa-bullseye' style={{ color: '#007bff' }} />}
        iconBg='tw:bg-blue-500/10'
        borderColor='blue-400'
        isLoading={isLoading}
      />
      <MetricCard
        label='Carbon Intensity'
        value={carbonIntensity.hasData ? (carbonIntensity.value ?? '--') : '--'}
        subtext={carbonIntensity.hasData ? carbonIntensity.unit : undefined}
        subtextNode={carbonIntensity.hasData ? undefined : (
          <Link to={businessMetricsLink} className='tw:text-sm tw:text-primary tw:no-underline tw:block'>
            Add employee data
          </Link>
        )}
        icon={<i className='fa fa-chart-line' style={{ color: '#6f42c1' }} />}
        iconBg='tw:bg-violet-500/10'
        borderColor='violet-400'
        isLoading={isLoading || intensityLoading}
      />
      <MetricCard
        label='Top Hotspot'
        value={summary ? topHotspot.label : '--'}
        subtext={summary ? `${topHotspot.percentage}% of total` : '--'}
        icon={<i className='fa fa-fire-flame-curved' style={{ color: '#fd7e14' }} />}
        iconBg='tw:bg-orange-500/10'
        borderColor='orange-400'
        isLoading={isLoading}
      />
    </div>
  );
};
